import { Button, Layout, Row, Switch, Typography, message } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useNavigate } from "react-router-dom";
import ArrowRight from "../../../assets/icons/arrow-right-white.svg";
import ArrowLeftIconComponent from "../../../components/ArrowLeftIconComponent";
import FanSubscriptionPageModel from "../../../components/FanSubscriptionPageModel/FanSubscriptionPageModel";
import SpinnerComponent from "../../../components/SpinnerComponent/SpinnerComponent";
import { IAllSubscription } from "../../../helpers/types";
import { subscriptionsService } from "../../../services";
import "./index.less";
import CancelledSubscriptionConfirmModalComponent from "../../../components/CancelledSubscriptionConfirmModalComponent";
import FreeSubscriptionCancelModal from "../../../components/freeSubscription/modals/FreeSubscriptionCancelModal";

const FanSubscriptionPage = () => {
  const { t } = useTranslation();
  const [fanSubscriptionModel, setFanSubscriptionModel] = useState(false);
  const [modifySubscriptionPlan, setModifySubscriptionPlan] = useState<IAllSubscription>();
  const [subscriptionData, setSubscriptionData] = useState<IAllSubscription[]>([]);
  const [skip, setSkip] = useState<any>(0);
  const [loading, setLoading] = useState<any>(false);
  const [hasNextPage, setHasNextPage] = useState<any>(false);
  const [totalData, setTotalData] = useState<any>(0);
  const navigate = useNavigate();
  const NB_USER_TO_SHOW = 10;
  const [cancelledSubscriptionConfirmModal, setCancelledSubscriptionConfirmModal] = useState<boolean>(false);
  const [cancelledSubscriptionLoading, setCancelledSubscriptionLoading] = useState<boolean>(false);
  const [showFreeSubscriptionCancelModal, setShowFreeSubscriptionCancelModal] = useState<boolean>(false);
  const [selectedCreatorForCancel, setSelectedCreatorForCancel] = useState<IAllSubscription | undefined>(undefined);

  async function subscriptions(NB_USER_TO_SHOW: any, skip: any) {
    setLoading(true);
    await subscriptionsService
      .allSubscriptions(NB_USER_TO_SHOW, skip)
      .then((response: any) => {
        setLoading(false);
        setTotalData(response?.total);
        if (subscriptionData.length === 0) {
          setSubscriptionData(response?.data);
        } else {
          setSubscriptionData((prevData) => Array.from(new Set([...prevData, ...(response?.data ?? [])])));
        }
      })
      .catch((error) => {
        setLoading(false);
        message.error(t("something-want-wrong"));
      });
  }

  async function loadMoreUsers() {
    const nextPage = skip + NB_USER_TO_SHOW;
    setSkip((prev: any) => prev + NB_USER_TO_SHOW);
    const totalPages = Math.ceil(totalData! / NB_USER_TO_SHOW);
    await subscriptions(NB_USER_TO_SHOW, nextPage);
    if (nextPage >= totalPages) {
      setHasNextPage(false);
    }
  }

  useEffect(() => {
    subscriptions(NB_USER_TO_SHOW, 0);
    setSubscriptionData([]);
  }, []);

  const unSubscribe = async (creatorId: string) => {
    setCancelledSubscriptionLoading(true);
    subscriptionsService
      .unsubscribeCreator(creatorId)
      .then((res) => {
        setCancelledSubscriptionLoading(false);
        setModifySubscriptionPlan(undefined);
        setCancelledSubscriptionConfirmModal(true);
      })
      .catch((error) => {
        message.error(t("something-want-wrong"));
        setModifySubscriptionPlan(undefined);
        setCancelledSubscriptionLoading(false);
      });
  };

  const getIt = () => {
    setCancelledSubscriptionConfirmModal(false);
    setSkip(0);
    setSubscriptionData([]);
    setHasNextPage(false);
    setTotalData(0);
    subscriptions(NB_USER_TO_SHOW, 0);
  };

  const [sentryRef] = useInfiniteScroll({
    loading: loading,
    hasNextPage,
    onLoadMore: loadMoreUsers,
  });

  return (
    <Layout className="gradient-background-subs">
      <Layout.Content className="content fanSubscriptionPageContentWrapper background-transparent">
        <Row className="relative justify-content-center header-title-container-70">
          <ArrowLeftIconComponent top={12} />
          <Typography.Text className="header-title-black-35">{t("my-creators")}</Typography.Text>
        </Row>
        {subscriptionData?.length === 0 && !loading && (
          <Typography.Text className="noSubscriptionsMessage">{t("you-are-not-subscribed-to-any-creator")}</Typography.Text>
        )}
        {subscriptionData?.map((item, index) => {
          const isExpired = item?.expiredAt ? new Date(item.expiredAt) < new Date() : false;
          const isDeactivatedNotExpired = item?.status === "deactivated" && !isExpired;
          const isDeactivatedExpired = item?.status === "deactivated" && isExpired;

          return (
            <div key={index} className="w-full mt-13 fan-creator-subs-container">
              <img className="fanSubscriptionPageAvatarImg" src={item?.creatorInfo?.avatar} alt="img" />
              <div className="fanSubscriptiondetailsWrapper">
                <Typography.Text
                  className={`fanSubscriptionCreatorName ${
                    isDeactivatedNotExpired ? "cancelled-not-expired" : isDeactivatedExpired ? "cancelled-expired" : ""
                  }`}
                >
                  {item?.creatorInfo?.name}
                </Typography.Text>
                <Typography.Text
                  className={`fanSubscriptionStatus ${
                    isDeactivatedNotExpired ? "cancelled-not-expired" : isDeactivatedExpired ? "cancelled-expired" : ""
                  }`}
                >
                  {item?.status === "active" ? t("renewal-active") : isDeactivatedNotExpired ? t("renewal-disabled") : t("subscription-cancelled")}
                </Typography.Text>
              </div>
              <Switch
                checked={item?.status === "active" || isDeactivatedNotExpired}
                onChange={(checked, e) => {
                  e.stopPropagation();
                  if (item?.creatorInfo?.isFreeSubscription && item?.status === "active") {
                    setSelectedCreatorForCancel(item);
                    setShowFreeSubscriptionCancelModal(true);
                  } else {
                    setFanSubscriptionModel(true);
                    setModifySubscriptionPlan(item);
                  }
                }}
                onClick={(checked, e) => {
                  e.stopPropagation();
                }}
                className={`subscription-switch ${
                  item?.status === "active" ? "active" : isDeactivatedNotExpired ? "cancelled-not-expired" : "cancelled-expired"
                }`}
              />
            </div>
          );
        })}
        {modifySubscriptionPlan ? (
          <FanSubscriptionPageModel
            fanSubscriptionModel={fanSubscriptionModel}
            modifySubscriptionPlan={modifySubscriptionPlan}
            setFanSubscriptionModel={setFanSubscriptionModel}
            unSubscribe={unSubscribe}
            isCreator={true}
            title={modifySubscriptionPlan.creatorInfo.name}
            isLoading={cancelledSubscriptionLoading}
          />
        ) : null}
        {totalData !== subscriptionData?.length && totalData !== 0 && subscriptionData?.length !== 0 && !loading && (
          <Row className={`mx-auto bg-black`}>
            <Typography.Text
              onClick={() => {
                // setSeeAllResults(true);
                setHasNextPage(true);
              }}
              className="SearchSeeAllResults mx-auto mt-20"
            >
              {t(`see-all-results`)}
            </Typography.Text>
          </Row>
        )}
        {(loading || hasNextPage) && <SpinnerComponent refProp={sentryRef} />}

        <CancelledSubscriptionConfirmModalComponent isOpen={cancelledSubscriptionConfirmModal} onClose={getIt} />
        {showFreeSubscriptionCancelModal && selectedCreatorForCancel && (
          <FreeSubscriptionCancelModal
            onClose={() => {
              setShowFreeSubscriptionCancelModal(false);
              setSelectedCreatorForCancel(undefined);
            }}
            onConfirm={() => {
              if (selectedCreatorForCancel?.creatorInfo?.id) {
                unSubscribe(selectedCreatorForCancel.creatorInfo.id);
                setShowFreeSubscriptionCancelModal(false);
              }
            }}
            isLoading={cancelledSubscriptionLoading}
            creatorId={selectedCreatorForCancel.creatorInfo?.id || ""}
          />
        )}
      </Layout.Content>
    </Layout>
  );
};

export default FanSubscriptionPage;
